import { useForm } from '@inertiajs/react';
import { Pencil } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { CredentialAssigneeOption, PrintedCredentialRow } from '@/types';
import {
  type CredentialFormData,
  credentialToForm,
  CredentialFormFields,
} from './CredentialFormFields';

interface EditCredentialDialogProps {
  eventSlug: string;
  credential: PrintedCredentialRow;
  assignableOptions: CredentialAssigneeOption[];
  credentialTypes: string[];
}

export function EditCredentialDialog({
  eventSlug,
  credential,
  assignableOptions,
  credentialTypes,
}: EditCredentialDialogProps) {
  const [open, setOpen] = useState(false);
  const form = useForm<CredentialFormData>(credentialToForm(credential));
  const { data, setData, processing } = form;
  const errors = form.errors as unknown as Record<string, string | undefined>;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    form.patch(`/admin/${eventSlug}/credentials/${credential.id}`, {
      preserveScroll: true,
      onSuccess: () => setOpen(false),
    });
  };

  return (
    <>
      <Button variant="ghost" size="icon" onClick={() => setOpen(true)}>
        <Pencil className="h-4 w-4" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit printed credential</DialogTitle>
            <DialogDescription>
              Repurpose this card by pointing it at somebody else — its QR keeps
              working and starts resolving to the new person.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <CredentialFormFields
              data={data}
              setData={setData}
              errors={errors}
              isPending={processing}
              assignableOptions={assignableOptions}
              credentialTypes={credentialTypes}
            />

            {errors.base && (
              <div className="rounded-md border border-red-200 bg-red-50 p-4">
                <p className="text-red-600 text-sm">{errors.base}</p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={processing}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={processing}>
                {processing ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
